import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    padding: 50,
    fontFamily: 'Helvetica',
    fontSize: 11,
    lineHeight: 1.7,
    color: '#1f2937',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 35,
    borderBottom: '2px solid #DB2777',
    paddingBottom: 20,
  },
  titleMain: { 
    fontSize: 28, 
    fontWeight: 'bold', 
    color: '#DB2777' 
  },
  subtitle: { 
    fontSize: 14, 
    color: '#831843', 
    marginTop: 5 
  },
  sectionTitle: {
    fontSize: 17,
    fontWeight: 'bold',
    color: '#831843',
    marginTop: 30,
    marginBottom: 15,
    borderLeft: '5px solid #DB2777',
    paddingLeft: 15,
  },
  text: { 
    fontSize: 11.5, 
    marginBottom: 9, 
    color: '#374151' 
  },
  row: { 
    flexDirection: 'row', 
    justifyContent: 'space-between', 
    marginBottom: 8 
  },
  label: { 
    fontWeight: 'bold', 
    width: '38%', 
    color: '#4B5563' 
  },
  value: { 
    width: '62%' 
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#FCE7F3',
    padding: '10px 12px',
    marginTop: 15,
    borderBottom: '2px solid #DB2777',
  },
  tableRow: {
    flexDirection: 'row',
    padding: '9px 12px',
    borderBottom: '1px solid #E5E7EB',
  },
  tableCell: { flex: 1 },
  pageNumber: {
    position: 'absolute',
    bottom: 35,
    right: 50,
    fontSize: 10,
    color: '#9CA3AF',
  },
});

const SHGPDF = ({ data }) => {
  const creationDate = data.createdDate 
    ? new Date(data.createdDate).toLocaleDateString('en-IN', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      }) 
    : new Date().toLocaleDateString('en-IN', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        {/* Header */}
        <View style={styles.header}>
          <View>
            <Text style={styles.titleMain}>FoundHer</Text>
            <Text style={styles.subtitle}>Self Help Group Document</Text>
          </View>
          <Text style={{ fontSize: 12, color: '#831843' }}>{creationDate}</Text>
        </View>

        <Text style={{ fontSize: 26, textAlign: 'center', marginBottom: 10, color: '#831843', fontWeight: 'bold' }}>
          {data.groupName}
        </Text>
        <Text style={{ fontSize: 16, textAlign: 'center', marginBottom: 35, color: '#831843' }}>
          {data.groupMotto || "Together We Rise 💖"}
        </Text>

        {/* Group Information */}
        <Text style={styles.sectionTitle}>Group Information</Text>
        <View style={styles.row}><Text style={styles.label}>Created On</Text><Text style={styles.value}>{creationDate}</Text></View>
        <View style={styles.row}><Text style={styles.label}>Creator</Text><Text style={styles.value}>{data.creatorName || 'N/A'}</Text></View>
        <View style={styles.row}>
          <Text style={styles.label}>Location</Text>
          <Text style={styles.value}>
            {[data.area, data.city, data.state, data.country, data.pincode].filter(Boolean).join(', ')}
          </Text>
        </View>

        {data.groupDesc && (
          <>
            <Text style={styles.sectionTitle}>Vision & Description</Text>
            <Text style={styles.text}>{data.groupDesc}</Text>
          </>
        )}

        {data.splPublicMessage && (
          <>
            <Text style={styles.sectionTitle}>Special Public Message</Text>
            <Text style={styles.text}>{data.splPublicMessage}</Text>
          </>
        )}

        {/* Members */}
        <Text style={styles.sectionTitle}>Group Members ({data.groupMembers?.length || 0})</Text>
        <View style={styles.tableHeader}>
          <Text style={[styles.tableCell, { flex: 2 }]}>Name</Text>
          <Text style={styles.tableCell}>Code</Text>
          <Text style={styles.tableCell}>Contribution</Text>
          <Text style={styles.tableCell}>KRA</Text>
          <Text style={styles.tableCell}>Status</Text>
        </View>

        {data.groupMembers?.map((member, index) => (
          <View key={index} style={styles.tableRow} wrap={false}>
            <Text style={[styles.tableCell, { flex: 2 }]}>{member.userName}</Text>
            <Text style={styles.tableCell}>{member.userCode}</Text>
            <Text style={styles.tableCell}>₹{member.contributionAmount || 0}</Text>
            <Text style={styles.tableCell}>{member.memberKRA || '-'}</Text>
            <Text style={styles.tableCell}>{member.isActive ? 'Active' : 'Inactive'}</Text>
          </View>
        ))}

        {/* Registrations */}
        {data.registrationNos?.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>Legal Registrations</Text>
            {data.registrationNos.map((reg, i) => (
              <View key={i} style={{ marginBottom: 18, padding: 15, backgroundColor: '#fdf4ff' }}>
                <Text style={{ fontWeight: 'bold', color: '#831843', marginBottom: 6 }}>{reg.type}</Text>
                <Text style={styles.text}>{reg.details}</Text>
                {reg.fromDate && <Text style={styles.text}>From: {new Date(reg.fromDate).toLocaleDateString('en-IN')}</Text>}
                {reg.expiryDate && <Text style={styles.text}>Valid Till: {new Date(reg.expiryDate).toLocaleDateString('en-IN')}</Text>}
              </View>
            ))}
          </>
        )}

        {/* Contact */}
        <Text style={styles.sectionTitle}>Contact Details</Text>
        <View style={styles.row}><Text style={styles.label}>Mobile</Text><Text style={styles.value}>{data.groupMobile || 'N/A'}</Text></View>
        <View style={styles.row}><Text style={styles.label}>WhatsApp</Text><Text style={styles.value}>{data.groupWhatsApp || 'N/A'}</Text></View>
        <View style={styles.row}><Text style={styles.label}>Email</Text><Text style={styles.value}>{data.groupEmail || 'N/A'}</Text></View>

        <Text style={{ marginTop: 50, textAlign: 'center', fontSize: 12, color: '#831843' }}>
          Together We Rise • FoundHer Private Self Help Groups
        </Text>

        {/* Dynamic Page Number */}
        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
      </Page>
    </Document>
  );
};

export default SHGPDF;